import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  CircularProgress,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Container,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { 
  Add, 
  Edit, 
  Delete,
  History,
  Person,
  Star,
  Close,
  FilterList,
  Analytics,
  Visibility as PreviewIcon,
  ExpandMore as ExpandMoreIcon,
  MonetizationOn as MoneyIcon,
  EmojiEvents as TrophyIcon,
  Casino as CasinoIcon,
} from '@mui/icons-material';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Grid } from '@mui/material';
import { galiAPI } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';


const GaliDeclareResults = () => {
  const { user } = useAuth();
  const [markets, setMarkets] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modal States
  const [declareModalOpen, setDeclareModalOpen] = useState(false);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [statsModalOpen, setStatsModalOpen] = useState(false);
  const [editDialog, setEditDialog] = useState(false);


  const [declareForm, setDeclareForm] = useState({
    market_id: '',
    result_date: new Date().toISOString().split('T')[0],
    left_digit: '',
    right_digit: ''
  });
  const [filters, setFilters] = useState({
    market_id: '',
    start_date: '',
    end_date: '',
    limit: '50'
  });
  const [resultSummary, setResultSummary] = useState(null);
  const [summaryDialog, setSummaryDialog] = useState(false);
  const [selectedResult, setSelectedResult] = useState(null);
  const [editForm, setEditForm] = useState({ left_digit: '', right_digit: '' });
  
  // Preview Winners State
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewExpanded, setPreviewExpanded] = useState({});

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const [marketsResponse, resultsResponse] = await Promise.all([
        galiAPI.getGaliMarkets(),
        galiAPI.getGaliResults({ limit: filters.limit })
      ]);
      setMarkets(marketsResponse.data?.markets || []);
      setResults(resultsResponse.data?.results || []);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to fetch initial data');
    } finally {
      setLoading(false);
    }
  };

  const fetchResults = async () => {
    try {
      setLoading(true);
      const params = { limit: filters.limit };
      if (filters.market_id) params.market_id = filters.market_id;
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      
      const response = await galiAPI.getGaliResults(params);
      setResults(response.data.results);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to fetch results');
    } finally {
        setLoading(false);
    }
  };

  const handleDeclareResult = async () => {
    try {
      const { market_id, result_date, left_digit, right_digit } = declareForm;

      if (!market_id || !result_date || left_digit === '' || right_digit === '') {
        return toast.error('All fields are required');
      }
      if (left_digit < 0 || left_digit > 9 || right_digit < 0 || right_digit > 9) {
        return toast.error('Digits must be between 0 and 9');
      }

      const response = await galiAPI.declareGaliResult({
        ...declareForm,
        left_digit: parseInt(left_digit),
        right_digit: parseInt(right_digit)
      });

      toast.success('Result declared successfully for all game types!');
      setDeclareModalOpen(false);
      
      if (response.data.summary) {
        setResultSummary(response.data.summary);
        setSummaryDialog(true);
      }
      
      setDeclareForm({ market_id: '', result_date: new Date().toISOString().split('T')[0], left_digit: '', right_digit: '' });
      
      fetchResults();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to declare result');
    }
  };
  
  const handleApplyFilters = () => {
    fetchResults();
    setFilterModalOpen(false);
  };
  
  const handleClearFilters = () => {
    setFilters({ market_id: '', start_date: '', end_date: '', limit: '50' });
    fetchResults(); // Refetch with cleared filters
    setFilterModalOpen(false);
  };

  const handleOpenEditDialog = (result) => {
    setSelectedResult(result);
    setEditForm({
      left_digit: result.left_digit?.toString() || '',
      right_digit: result.right_digit?.toString() || ''
    });
    setEditDialog(true);
  };

  const handleUpdateResult = async () => {
    try {
      if (editForm.left_digit === '' || editForm.right_digit === '') return toast.error('Both digits are required');
      if (editForm.left_digit < 0 || editForm.left_digit > 9 || editForm.right_digit < 0 || editForm.right_digit > 9) return toast.error('Digits must be between 0-9');

      await galiAPI.updateGaliResult(selectedResult.id, {
        left_digit: parseInt(editForm.left_digit),
        right_digit: parseInt(editForm.right_digit)
      });

      toast.success('Result updated successfully! Bets are being re-processed.');
      setEditDialog(false);
      fetchResults();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update result');
    }
  };

  const handleDeleteResult = async (id) => {
    if (window.confirm('Are you sure? This will reset all related bets to pending.')) {
      try {
        await galiAPI.deleteGaliResult(id);
        toast.success('Result deleted successfully');
        fetchResults();
      } catch (err) {
        toast.error(err.response?.data?.message || 'Failed to delete result');
      }
    }
  };

  const handlePreviewWinners = async () => {
    try {
      const { market_id, result_date, left_digit, right_digit } = declareForm;

      if (!market_id) {
        return toast.error('Please select a market first');
      }
      if (left_digit === '' || right_digit === '') {
        return toast.error('Please enter both left and right digits');
      }
      if (left_digit < 0 || left_digit > 9 || right_digit < 0 || right_digit > 9) {
        return toast.error('Digits must be between 0 and 9');
      }

      setPreviewLoading(true);

      const payload = {
        market_id,
        result_date: result_date || new Date().toISOString().split('T')[0],
        left_digit: parseInt(left_digit),
        right_digit: parseInt(right_digit)
      };

      const response = await galiAPI.previewGaliWinners(payload);

      if (response.data?.success) {
        setPreviewData(response.data.preview);
        setPreviewExpanded({});
        setPreviewDialogOpen(true);
      } else {
        toast.error(response.data?.message || 'Failed to preview winners');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to preview winners');
    } finally {
      setPreviewLoading(false);
    }
  };

  const columns = [
    { field: 'id', headerName: 'ID', width: 70 },
    { 
      field: 'result_date', 
      headerName: 'Result Date',
      width: 150,
      valueGetter: (params) => {
        if (!params || !params.row) return null;
        return params.row.result_date || null;
      },
      renderCell: (params) => {
        if (!params?.row?.result_date) return 'N/A';
        const date = new Date(params.row.result_date);
        return date.toLocaleDateString('en-IN', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      }
    },
    { field: 'market', headerName: 'Gali Market', flex: 1, minWidth: 150, renderCell: ({ value }) => value?.market_name || 'N/A' },
    {
      field: 'result',
      headerName: 'Result',
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={1}>
          <Chip 
            label={params.row.left_digit ?? '-'}
            sx={{ bgcolor: '#6366f1', color: 'white', fontWeight: 'bold' }}
          />
          <Chip 
            label={params.row.right_digit ?? '-'}
            sx={{ bgcolor: '#10b981', color: 'white', fontWeight: 'bold' }}
          />
        </Stack>
      )
    },
    { field: 'declarer', headerName: 'Declared By', width: 150, renderCell: ({ value }) => value?.username || 'N/A' },
    {
      field: 'declared_at',
      headerName: 'Declared At',
      width: 200,
      valueGetter: (params) => {
        if (!params || !params.row) return null;
        return params.row.declared_at || null;
      },
      renderCell: (params) => {
        if (!params?.row?.declared_at) return 'N/A';
        const date = new Date(params.row.declared_at);
        return date.toLocaleString('en-IN', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
    },
    {
      field: 'actions', headerName: 'Actions', width: 120, sortable: false,
      renderCell: (params) => user.user_type === 'owner' && (
        <Stack direction="row">
          <Tooltip title="Edit"><IconButton size="small" color="primary" onClick={() => handleOpenEditDialog(params.row)}><Edit /></IconButton></Tooltip>
          <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => handleDeleteResult(params.row.id)}><Delete /></IconButton></Tooltip>
        </Stack>
      ),
    },
  ];

  return (
    <Box sx={{ width: '100%', minHeight: '100vh' }}>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Stack spacing={3}>
            <Stack direction={{xs: 'column', md: 'row'}} justifyContent="space-between" alignItems={{xs: 'flex-start', md: 'center'}} spacing={2}>
                <Box>
                    <Typography variant="h4" fontWeight="bold">Gali Results Management</Typography>
                    <Typography color="text.secondary">Oversee historical data and declare new results.</Typography>
                </Box>
                <Stack direction="row" spacing={1.5}>
                    <Button variant="contained" startIcon={<Add />} onClick={() => setDeclareModalOpen(true)}>Declare Result</Button>
                    <Button variant="outlined" startIcon={<FilterList />} onClick={() => setFilterModalOpen(true)}>Filters</Button>
                    <Button variant="outlined" startIcon={<Analytics />} onClick={() => setStatsModalOpen(true)}>Stats</Button>
                </Stack>
            </Stack>

            <Card sx={{ borderRadius: 3 }}>
                <CardContent>
                    <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <History /> Gali Past Results
                    </Typography>
                    <Box sx={{ height: 'calc(100vh - 300px)', width: '100%' }}>
                        {loading ? (
                             <Stack alignItems="center" justifyContent="center" height="100%"><CircularProgress /><Typography mt={2}>Loading Results...</Typography></Stack>
                        ) : (
                            <DataGrid 
                                rows={results} 
                                columns={columns} 
                                pageSize={25} 
                                rowsPerPageOptions={[10, 25, 50, 100]} 
                                disableSelectionOnClick 
                                sx={{ 
                                    border: 'none', 
                                    '& .MuiDataGrid-columnHeaders': { 
                                        backgroundColor: (theme) => theme.palette.action.hover 
                                    } 
                                }} 
                            />
                        )}
                    </Box>
                </CardContent>
            </Card>
        </Stack>
      </Container>

      {/* Declare Result Modal */}
      <Dialog open={declareModalOpen} onClose={() => setDeclareModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{fontWeight: 'bold'}}>Declare New Result</DialogTitle>
        <DialogContent>
            <Stack spacing={3} sx={{pt: 1}}>
                <FormControl fullWidth>
                    <InputLabel>Gali Market</InputLabel>
                    <Select value={declareForm.market_id} onChange={(e) => setDeclareForm({...declareForm, market_id: e.target.value})} label="Gali Market">
                        <MenuItem value=""><em>Select a Market</em></MenuItem>
                {markets.map((m) => <MenuItem key={m.id} value={m.id}>{m.market_name}</MenuItem>)}
                    </Select>
                </FormControl>
            <TextField
              fullWidth
              label="Result Date"
              type="date"
              value={declareForm.result_date}
              onChange={(e) => setDeclareForm({ ...declareForm, result_date: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
            <Typography variant="body2" color="info.main" sx={{ p: 1.5, bgcolor: 'info.main', bgcolor: 'rgba(33, 150, 243, 0.1)', borderRadius: 1, border: '1px solid', borderColor: 'info.main' }}>
              This will declare result for <strong>all game types</strong> (JANTRI, CROSS, OPEN) at once.
            </Typography>
                <Stack direction="row" spacing={2} alignItems="center">
              <TextField
                fullWidth
                label="Left Digit (Andar)"
                type="number"
                value={declareForm.left_digit}
                onChange={(e) => setDeclareForm({ ...declareForm, left_digit: e.target.value })}
                inputProps={{ min: 0, max: 9, style: { textAlign: 'center', fontSize: '1.5rem' } }}
              />
              <TextField
                fullWidth
                label="Right Digit (Bahar)"
                type="number"
                value={declareForm.right_digit}
                onChange={(e) => setDeclareForm({ ...declareForm, right_digit: e.target.value })}
                inputProps={{ min: 0, max: 9, style: { textAlign: 'center', fontSize: '1.5rem' } }}
              />
                </Stack>
            </Stack>
        </DialogContent>
        <DialogActions sx={{p: 2}}>
            <Button onClick={() => setDeclareModalOpen(false)}>Cancel</Button>
            <Button
              variant="outlined"
              color="info"
              onClick={handlePreviewWinners}
              disabled={previewLoading || !declareForm.market_id || declareForm.left_digit === '' || declareForm.right_digit === ''}
              startIcon={<PreviewIcon />}
            >
              {previewLoading ? 'Loading...' : 'Preview Winners'}
            </Button>
          <Button
            variant="contained"
            onClick={handleDeclareResult}
            disabled={!declareForm.market_id || !declareForm.result_date || declareForm.left_digit === '' || declareForm.right_digit === ''}
          >
            Declare
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Winners Dialog */}
      <Dialog
        open={previewDialogOpen}
        onClose={() => setPreviewDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { height: '90vh', maxHeight: '900px' }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box display="flex" alignItems="center" gap={1}>
            <PreviewIcon color="info" />
            <Typography variant="h6">Preview Potential Winners</Typography>
          </Box>
          <IconButton onClick={() => setPreviewDialogOpen(false)}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {previewData && (
            <Box>
              {/* Summary Cards */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.lighter' }}>
                    <Typography variant="h4" color="primary.main" fontWeight="bold">
                      {previewData.summary?.total_pending_bets || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">Total Pending Bets</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.lighter' }}>
                    <Typography variant="h4" color="success.main" fontWeight="bold">
                      {previewData.summary?.winning_bets || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">Potential Winners</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'error.lighter' }}>
                    <Typography variant="h4" color="error.main" fontWeight="bold">
                      ₹{(previewData.summary?.total_potential_payout || 0).toLocaleString('en-IN')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">Total Payout</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: previewData.summary?.potential_profit_loss >= 0 ? 'success.lighter' : 'error.lighter' }}>
                    <Typography variant="h4" color={previewData.summary?.potential_profit_loss >= 0 ? 'success.main' : 'error.main'} fontWeight="bold">
                      ₹{(previewData.summary?.potential_profit_loss || 0).toLocaleString('en-IN')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">Potential Profit/Loss</Typography>
                  </Paper>
                </Grid>
              </Grid>

              {/* Simulated Result Display */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <CasinoIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Simulated Result for {previewData.market?.name}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6} md={3}>
                      <Typography variant="body2" color="text.secondary">Left Digit (Andar)</Typography>
                      <Typography variant="h4" color="primary.main">
                        {previewData.simulated_result?.left_digit ?? '—'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Typography variant="body2" color="text.secondary">Right Digit (Bahar)</Typography>
                      <Typography variant="h4" color="secondary.main">
                        {previewData.simulated_result?.right_digit ?? '—'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Typography variant="body2" color="text.secondary">Result Number</Typography>
                      <Typography variant="h4" color="success.main">
                        {previewData.simulated_result?.result_number || '—'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Typography variant="body2" color="text.secondary">Sum Digit</Typography>
                      <Typography variant="h4" color="info.main">
                        {previewData.simulated_result?.sum_digit ?? '—'}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Winners by User */}
              {previewData.winners_by_user && previewData.winners_by_user.length > 0 && (
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      <TrophyIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'warning.main' }} />
                      Winners by User ({previewData.winners_by_user.length} users)
                    </Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>User</TableCell>
                            <TableCell align="center">Winning Bets</TableCell>
                            <TableCell align="right">Bet Amount</TableCell>
                            <TableCell align="right">Potential Win</TableCell>
                            <TableCell align="center">Details</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {previewData.winners_by_user.map((userWin, idx) => (
                            <React.Fragment key={idx}>
                              <TableRow 
                                hover 
                                sx={{ cursor: 'pointer', bgcolor: previewExpanded[idx] ? 'action.selected' : 'inherit' }}
                                onClick={() => setPreviewExpanded(prev => ({ ...prev, [idx]: !prev[idx] }))}
                              >
                                <TableCell>
                                  <Box display="flex" alignItems="center" gap={1}>
                                    <Person fontSize="small" color="primary" />
                                    <Box>
                                      <Typography variant="body2" fontWeight="bold">{userWin.user?.username}</Typography>
                                      <Typography variant="caption" color="text.secondary">{userWin.user?.user_type}</Typography>
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell align="center">
                                  <Chip size="small" label={userWin.total_bets} color="primary" />
                                </TableCell>
                                <TableCell align="right">₹{userWin.total_bet_amount?.toLocaleString('en-IN')}</TableCell>
                                <TableCell align="right">
                                  <Typography color="success.main" fontWeight="bold">
                                    ₹{userWin.total_potential_win?.toLocaleString('en-IN')}
                                  </Typography>
                                </TableCell>
                                <TableCell align="center">
                                  <IconButton size="small">
                                    <ExpandMoreIcon sx={{ transform: previewExpanded[idx] ? 'rotate(180deg)' : 'rotate(0deg)', transition: '0.2s' }} />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                              {/* Expanded bet details */}
                              {previewExpanded[idx] && userWin.bets?.map((bet, betIdx) => (
                                <TableRow key={`${idx}-${betIdx}`} sx={{ bgcolor: 'grey.50' }}>
                                  <TableCell colSpan={5} sx={{ pl: 6 }}>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                                      <Chip size="small" label={bet.game_mode} variant="outlined" />
                                      <Typography variant="body2">
                                        <strong>Value:</strong> {bet.generated_value} | <strong>Category:</strong> {bet.bet_category}
                                      </Typography>
                                      <Typography variant="body2">
                                        <strong>Bet:</strong> ₹{bet.bet_amount} × {bet.multiplier} = 
                                        <Typography component="span" color="success.main" fontWeight="bold" sx={{ ml: 0.5 }}>
                                          ₹{bet.potential_win_amount?.toLocaleString('en-IN')}
                                        </Typography>
                                      </Typography>
                                      <Typography variant="caption" color="info.main">
                                        {bet.win_reason}
                                      </Typography>
                                    </Box>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </React.Fragment>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              )}

              {/* No Winners Message */}
              {(!previewData.winners_by_user || previewData.winners_by_user.length === 0) && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <TrophyIcon sx={{ fontSize: 60, color: 'grey.400', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">No Potential Winners</Typography>
                  <Typography variant="body2" color="text.secondary">
                    No bets match the simulated result for this market and date.
                  </Typography>
                </Box>
              )}

              {/* All Potential Winners Table */}
              {previewData.potential_winners && previewData.potential_winners.length > 0 && (
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      <MoneyIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'success.main' }} />
                      All Winning Bets ({previewData.potential_winners.length})
                    </Typography>
                    <TableContainer sx={{ maxHeight: 300 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>Bet ID</TableCell>
                            <TableCell>User</TableCell>
                            <TableCell>Game Mode</TableCell>
                            <TableCell>Value</TableCell>
                            <TableCell>Category</TableCell>
                            <TableCell align="right">Bet Amt</TableCell>
                            <TableCell align="right">Win Amt</TableCell>
                            <TableCell>Reason</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {previewData.potential_winners.map((winner, idx) => (
                            <TableRow key={idx} hover>
                              <TableCell>{winner.bet_id}</TableCell>
                              <TableCell>{winner.user?.username}</TableCell>
                              <TableCell><Chip size="small" label={winner.game_mode} /></TableCell>
                              <TableCell><strong>{winner.generated_value}</strong></TableCell>
                              <TableCell>{winner.bet_category}</TableCell>
                              <TableCell align="right">₹{winner.bet_amount}</TableCell>
                              <TableCell align="right">
                                <Typography color="success.main" fontWeight="bold">
                                  ₹{winner.potential_win_amount?.toLocaleString('en-IN')}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Tooltip title={winner.win_reason}>
                                  <Typography variant="caption" noWrap sx={{ maxWidth: 150, display: 'block' }}>
                                    {winner.win_reason}
                                  </Typography>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialogOpen(false)}>Close</Button>
          <Button 
            variant="contained" 
            color="primary"
            onClick={() => setPreviewDialogOpen(false)}
          >
            Proceed to Declare
          </Button>
        </DialogActions>
      </Dialog>

      {/* Filter Modal */}
      <Dialog open={filterModalOpen} onClose={() => setFilterModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{fontWeight: 'bold'}}>Filter Past Results</DialogTitle>
        <DialogContent>
            <Stack spacing={2} sx={{pt: 1}}>
                <FormControl fullWidth><InputLabel>Market</InputLabel><Select name="market_id" value={filters.market_id} onChange={(e) => setFilters({...filters, market_id: e.target.value})} label="Market"><MenuItem value=""><em>All Markets</em></MenuItem>{markets.map((m) => <MenuItem key={m.id} value={m.id}>{m.market_name}</MenuItem>)}</Select></FormControl>
                <TextField fullWidth name="start_date" label="Start Date" type="date" value={filters.start_date} onChange={(e) => setFilters({...filters, start_date: e.target.value})} InputLabelProps={{ shrink: true }} />
                <TextField fullWidth name="end_date" label="End Date" type="date" value={filters.end_date} onChange={(e) => setFilters({...filters, end_date: e.target.value})} InputLabelProps={{ shrink: true }} />
                <FormControl fullWidth><InputLabel>Limit</InputLabel><Select name="limit" value={filters.limit} onChange={(e) => setFilters({...filters, limit: e.target.value})} label="Limit"><MenuItem value="25">25</MenuItem><MenuItem value="50">50</MenuItem><MenuItem value="100">100</MenuItem><MenuItem value="200">200</MenuItem></Select></FormControl>
            </Stack>
        </DialogContent>
        <DialogActions sx={{p: 2, justifyContent: 'space-between'}}>
            <Button variant="outlined" color="error" onClick={handleClearFilters}>Clear Filters</Button>
            <Stack direction="row" spacing={1}>
                <Button onClick={() => setFilterModalOpen(false)}>Cancel</Button>
                <Button variant="contained" onClick={handleApplyFilters}>Apply Filters</Button>
            </Stack>
        </DialogActions>
      </Dialog>

      {/* Statistics Modal */}
      <Dialog open={statsModalOpen} onClose={() => setStatsModalOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{fontWeight: 'bold'}}>Results Statistics</DialogTitle>
        <DialogContent>
            <Stack spacing={2} sx={{pt: 1}}>
                <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', borderRadius: 2, borderColor: '#6366f1' }}>
                    <Typography variant="h4" sx={{ color: '#6366f1' }} fontWeight="bold">{results.length}</Typography>
                    <Typography variant="body2" color="text.secondary">Total Results Loaded</Typography>
                </Paper>
                <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', borderRadius: 2, borderColor: '#10b981' }}>
                    <Typography variant="h4" sx={{ color: '#10b981' }} fontWeight="bold">{markets.length}</Typography>
                    <Typography variant="body2" color="text.secondary">Active Markets</Typography>
                </Paper>
            </Stack>
        </DialogContent>
        <DialogActions sx={{p: 2}}><Button variant="contained" onClick={() => setStatsModalOpen(false)}>Close</Button></DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Edit /> Edit Result</DialogTitle>
        <DialogContent>
          {selectedResult && (
            <Stack spacing={2} sx={{ minWidth: { xs: 'auto', sm: '300px' }, pt: 1 }}>
              <Typography><strong>Market:</strong> {selectedResult.market?.market_name}</Typography>
              <Typography><strong>Date:</strong> {selectedResult.result_date ? new Date(selectedResult.result_date).toLocaleDateString() : 'N/A'}</Typography>
              <Typography variant="body2" color="warning.main" sx={{ p: 1.5, bgcolor: 'rgba(255, 152, 0, 0.1)', borderRadius: 1, border: '1px solid', borderColor: 'warning.main' }}>
                Editing this result will reset and re-process all related bets for ALL game types.
              </Typography>
              <Stack direction="row" spacing={2} pt={1}>
                <TextField
                  fullWidth
                  label="Left Digit"
                  type="number"
                  value={editForm.left_digit}
                  onChange={(e) => setEditForm({ ...editForm, left_digit: e.target.value })}
                  inputProps={{ min: 0, max: 9 }}
                />
                <TextField
                  fullWidth
                  label="Right Digit"
                  type="number"
                  value={editForm.right_digit}
                  onChange={(e) => setEditForm({ ...editForm, right_digit: e.target.value })}
                  inputProps={{ min: 0, max: 9 }}
                />
              </Stack>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setEditDialog(false)}>Cancel</Button>
          <Button
            onClick={handleUpdateResult}
            variant="contained"
            disabled={editForm.left_digit === '' || editForm.right_digit === ''}
          >
            Update Result
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GaliDeclareResults;